import { Table, Card, Typography, Tag, Tooltip, Space, Tabs } from "antd";
import { useMemo, useState } from "react";
import dayjs from "dayjs";

const { Text } = Typography;

const formatNumber = (num) =>
  new Intl.NumberFormat("es-CO").format(num);

export default function DashboardTable({
  movimientos,
  stock,
  alertas,
  loading
}) {
  const [activeTab, setActiveTab] = useState("entregas");

  const [pageSize, setPageSize] = useState(10);

  const renderProducto = (producto, record, width = 280) => (
    <Tooltip title={producto}>
      <Space direction="vertical" size={0} style={{ maxWidth: width }}>
        {record.codigo && <Text strong>{record.codigo}</Text>}
        <Text
          strong
          style={{
            display: "block",
            color: "#042956",
            fontSize: 14,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: width
          }}
        >
          {producto}
        </Text>
        {record.Numero_PO && (
          <Text type="secondary">PO: {record.Numero_PO}</Text>
        )}
      </Space>
    </Tooltip>
  );

  const columnasEntregas = [
    {
      title: "Fecha",
      dataIndex: "fecha_entrega",
      width: 110,
      render: (fecha) => dayjs(fecha).format("DD/MM/YYYY"),
      sorter: (a, b) =>
        new Date(a.fecha_entrega) - new Date(b.fecha_entrega)
    },
    {
      title: "Producto",
      dataIndex: "nombre_producto",
      width: 300,
      render: (producto, record) =>
        renderProducto(producto, record,)
    },
    {
      title: "Cantidad",
      dataIndex: "cantidad",
      width: 90,
      align: "center",
      render: (cantidad) => (
        <Tag color="blue">
          {formatNumber(Number(cantidad))}
        </Tag>
      )
    },
    {
      title: "Entidad",
      width: 300,
      render: (_, record) => (
        <Space direction="vertical" size={0} style={{ maxWidth: 280 }}>

          <Tooltip title={record.nombre}>
            <Text
              strong
              style={{
                display: "block",
                maxWidth: 280,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
              }}
            >
              {record.nombre}
            </Text>
          </Tooltip>

          <Text type="secondary">
            {record.tipo_entrega === "Participante"
              ? `CC ${record.identificacion}`
              : `NIT ${record.identificacion}`}
          </Text>

        </Space>
      )
    },
    {
      title: "Ubicación",
      width: 150,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{record.departamento}</Text>
          <Text type="secondary">{record.municipio}</Text>
        </Space>
      )
    },
    {
      title: "Proyecto",
      dataIndex: "proyecto",
      ellipsis: true,
      render: (proyecto) => (
        <Tooltip title={proyecto}>
          <Text>{proyecto}</Text>
        </Tooltip>
      )
    },
    {
      title: "Tipo entrega",
      dataIndex: "tipo_entrega",
      width: 100,
      align: "center",
      render: (tipo) => (
        <Tag
          color={
            tipo === "Organización"
              ? "purple"
              : "green"
          }
        >
          {tipo}
        </Tag>
      )
    }
  ];

  const columnasProductos = [
    {
      title: "Producto",
      dataIndex: "nombre_producto",
      width: 350,

      render: (producto, record) => (
        <Space direction="vertical" size={0}>
          {renderProducto(producto, record, 320)}
        </Space>
      )
    },
    {
      title: "Comprado",
      dataIndex: "cantidad_comprada",
      align: "center",
      render: (v) => (
        <Tag color="blue">
          {formatNumber(Number(v))}
        </Tag>
      )
    },
    {
      title: "Entregado",
      dataIndex: "cantidad_entregada",
      align: "center",
      render: (v) => (
        <Tag color="purple">
          {formatNumber(Number(v))}
        </Tag>
      )
    },
    {
      title: "Stock",
      dataIndex: "stock_disponible",
      align: "center",
      render: (v) => {

        const stock = Number(v);

        return (
          <Tag color={stock > 0 ? "green" : "red"}>
            {formatNumber(stock)}
          </Tag>
        );
      }
    },
    {
      title: "Precio unitario",
      dataIndex: "valor_unitario_COP",
      align: "right",
      render: (v) => (
        <Text strong>
          ${formatNumber(Number(v))}
        </Text>
      )
    },
    {
      title: "Valor total",
      align: "right",
      render: (_, record) => {

        const total =
          Number(record.cantidad_comprada) *
          Number(record.valor_unitario_COP);

        return (
          <Text strong style={{ color: "#042956" }}>
            ${formatNumber(total)}
          </Text>
        );
      }
    }
  ];

  const columnasAlertas = [
    {
      title: "Producto",
      dataIndex: "nombre_producto",
      width: 350,
      render: (producto, record) =>
        renderProducto(producto, record, 320)
    },
    {
      title: "Comprado",
      dataIndex: "cantidad_comprada",
      align: "center",
      render: (v) => (
        <Tag color="blue">
          {formatNumber(Number(v))}
        </Tag>
      )
    },
    {
      title: "Entregado",
      dataIndex: "cantidad_entregada",
      align: "center",
      render: (v) => (
        <Tag color="purple">
          {formatNumber(Number(v))}
        </Tag>
      )
    },
    {
      title: "Stock disponible",
      dataIndex: "stock_disponible",
      align: "center",
      render: (v) => (
        <Tag color="red">
          {formatNumber(Number(v))}
        </Tag>
      )
    },
    {
      title: "Valor comprometido",
      dataIndex: "monto_disponible_COP",
      align: "right",
      render: (v) => (
        <Text strong style={{ color: "#CF1322" }}>
          ${formatNumber(Number(v))}
        </Text>
      )
    },
    {
      title: "Estado",
      dataIndex: "estado_stock",
      align: "center",
      render: (estado) => {

        const color =
          estado === "CRITICO"
            ? "red"
            : estado === "BAJO"
              ? "orange"
              : "green";

        return <Tag color={color}>{estado}</Tag>;
      }
    }
  ];

  const dataEntregas = useMemo(
    () =>
      movimientos.map((m, i) => ({
        ...m,
        key: i
      })),
    [movimientos]
  );

  const dataProductos = useMemo(
    () =>
      stock.map((s, i) => ({
        ...s,
        key: i
      })),
    [stock]
  );

  const dataAlertas = useMemo(
    () =>
      alertas
        .filter(a => Number(a.stock_disponible) < 0) 
        .map((a, i) => ({
          ...a,
          key: i
        })),
    [alertas]
  );

  const dataNovedad = useMemo(
  () =>
    alertas
      .filter(a => Number(a.stock_disponible) === 0) 
      .map((a, i) => ({
        ...a,
        key: i
      })),
  [alertas]
);

 const columnasNovedad = [
  {
    title: "Producto",
    dataIndex: "nombre_producto",
    width: 350,
    render: (producto, record) =>
      renderProducto(producto, record, 320)
  },
  {
    title: "Comprado",
    dataIndex: "cantidad_comprada",
    align: "center",
    render: (v) => (
      <Tag color="blue">
        {formatNumber(Number(v))}
      </Tag>
    )
  },
  {
    title: "Entregado",
    dataIndex: "cantidad_entregada",
    align: "center",
    render: (v) => (
      <Tag color="purple">
        {formatNumber(Number(v))}
      </Tag>
    )
  },
  {
    title: "Stock disponible",
    dataIndex: "stock_disponible",
    align: "center",
    render: (v) => (
      <Tag color="orange">
        {formatNumber(Number(v))}
      </Tag>
    )
  },
  {
    title: "Estado",
    dataIndex: "estado_stock",
    align: "center",
    render: () => <Tag color="orange">AGOTADO</Tag>
  }
];


  const paginationConfig = {
    pageSize,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "30", "40", "50"],
    showTotal: (total) => `Total: ${total}`,
    onShowSizeChange: (_, size) => setPageSize(size)
  };

  const tabs = [
    {
      key: "entregas",
      label: `Entregas (${formatNumber(movimientos.length)})`,
      children: (
        <Table
          bordered
          columns={columnasEntregas}
          dataSource={dataEntregas}
          loading={loading}
          size="small"
          sticky
          rowHoverable
          pagination={paginationConfig}
          scroll={{ x: 1200 }}
        />
      )
    },
    {
      key: "productos",
      label: `Productos (${formatNumber(stock.length)})`,
      children: (
        <Table
          bordered
          columns={columnasProductos}
          dataSource={dataProductos}
          loading={loading}
          size="small"
          sticky
          rowHoverable
          pagination={paginationConfig}
        />
      )
    },
    {
      key: "alertas",
      label: `Alertas (${formatNumber(
        alertas.filter(a => Number(a.stock_disponible) < 0).length
      )})`,
      children: (
        <Table
          bordered
          columns={columnasAlertas}
          dataSource={dataAlertas}
          loading={loading}
          size="small"
          sticky
          rowHoverable
          pagination={paginationConfig}
        />
      )
    },
    {
  key: "novedad",
  label: `Novedad (${formatNumber(
    alertas.filter(a => Number(a.stock_disponible) === 0).length
  )})`,
  children: (
    <Table
      bordered
      columns={columnasNovedad}
      dataSource={dataNovedad}
      loading={loading}
      size="small"
      sticky
      rowHoverable
      pagination={paginationConfig}
    />
  )
},
  ];

 
  return (
    <Card
      title={
        <span style={{ color: "#FFFFFF" }}>
          Gestión de inventario
        </span>
      }
      style={{
        borderRadius: 12,
        border: "1px solid #f0f0f0",
        marginTop: 30,
        marginBottom: 24
      }}
      styles={{
        header: {
          background: "#042956",
          borderBottom: "none"
        }
      }}
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabs}
        centered
        size="large"
      />
    </Card>
  );
}